import { useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type QuotationDetailDto } from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useQuotation,
  useQuotationAction,
  useDeleteQuotation,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { QuotationDocument } from "@/components/quotations/QuotationDocument";
import { buildQuotationHtml, printQuotation } from "@/lib/printQuotation";
import { exportQuotationCsv, quotationStatus } from "@/lib/quotationCsv";
import { DocumentExportDialog } from "@/components/domain/DocumentExportDialog";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { formatDateTime } from "@/domain/datetime";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";

export function QuotationDetailPage() {
  const { id } = useParams();
  return <QuotationDetail key={id} />;
}
function QuotationDetail() {
  const { id } = useParams();
  const { search: context } = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const result = useQuotation(
    id,
    new URLSearchParams(context).get("archived") === "true",
  );
  const q = result.data;
  const action = useQuotationAction(id ?? "");
  const remove = useDeleteQuotation();
  const [confirm, setConfirm] = useState<
    "publish" | "revise" | "archive" | null
  >(null);
  const [preview, setPreview] = useState<QuotationDetailDto | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [loadingRevision, setLoadingRevision] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  usePageCrumbs(q ? [{ label: q.quotationNo }] : result.error ? [{ label: t("common.unavailable") }] : [], `/quotations${context}`);
  if (result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (result.error || !q)
    return (
      <PageBody>
        <ErrorState
          message={t("quotations.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  const canUpdate =
    Boolean(user && can("records:update")) && !q.deletedAt;
  const canDelete =
    Boolean(user && can("records:delete")) && !q.deletedAt;
  const status = quotationStatus(q);
  const busy = action.isPending || remove.isPending;
  const runAction = async () => {
    if (!confirm || lock.current) return;
    lock.current = true;
    setError(null);
    try {
      if (confirm === "archive") {
        await remove.mutateAsync({ id: q.id, expectedUpdatedAt: q.updatedAt });
        navigate(`/quotations${context}`);
      } else {
        await action.mutateAsync({
          action: confirm,
          expectedUpdatedAt: q.updatedAt,
        });
        if (confirm === "revise")
          navigate(`/quotations/${q.id}/edit${context}`);
      }
      setConfirm(null);
    } catch (err) {
      setError(getLocalizedError(err, t, "quotations.errorSave"));
      setConfirm(null);
    } finally {
      lock.current = false;
    }
  };
  const showRevision = async (revision: number) => {
    if (loadingRevision !== null) return;
    setLoadingRevision(revision);
    setError(null);
    try {
      const response = await apiFetch<{ data: QuotationDetailDto }>(
        `/quotations/${q.id}/revisions/${revision}`,
      );
      setPreview(response.data);
    } catch (err) {
      setError(getLocalizedError(err, t, "quotations.errorLoad"));
    } finally {
      setLoadingRevision(null);
    }
  };
  return (
    <PageBody>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-mono text-xl font-semibold">{q.quotationNo}</h1>
          <Badge
            tone={
              status === "Expired"
                ? "warn"
                : status === "Issued"
                  ? "info"
                  : "neutral"
            }
          >
            {t(
              q.workflowVersion === 0
                ? "quotationWorkspace.legacyReview"
                : `quotationWorkspace.status_${status}`,
            )}
          </Badge>
          <span className="text-xs text-ink-soft">
            {t("quotationWorkspace.revision")}{" "}
            <span className="font-mono">{q.revision}</span>
          </span>
          {q.deletedAt && <Badge>{t("quotationWorkspace.archived")}</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setPreview(q)}>
            {t("quotationWorkspace.preview")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-haspopup="dialog"
            onClick={() => setExportOpen(true)}
          >
            {t("common.export")}
          </Button>
          {canUpdate && q.status === "Draft" && (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => navigate(`/quotations/${q.id}/edit${context}`)}
            >
              {t("common.edit")}
            </Button>
          )}
          {canUpdate && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                setConfirm(q.status === "Draft" ? "publish" : "revise")
              }
            >
              {t(
                q.status === "Draft"
                  ? "quotationWorkspace.publish"
                  : "quotationWorkspace.revise",
              )}
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={() => setConfirm("archive")}
            >
              {t("quotationWorkspace.archive")}
            </Button>
          )}
        </div>
      </div>
      <p className="mb-4 text-xs text-ink-soft">
        {t(
          q.deletedAt
            ? "quotationWorkspace.archivedHint"
            : q.status === "Draft"
              ? "quotationWorkspace.draftHint"
              : "quotationWorkspace.issuedHint",
        )}
      </p>
      {error && (
        <p
          role="alert"
          className="mb-3 rounded-sm bg-danger-tint p-3 text-sm text-danger"
        >
          {error}
        </p>
      )}
      {q.workflowVersion === 0 && (
        <p className="mb-3 text-xs text-ink-soft">
          {t("quotationWorkspace.legacyReviewHint")}
        </p>
      )}
      {q.validUntil != null && (
        <p className="mb-3 text-xs text-ink-soft">
          {t("quotationWorkspace.validityOverrideHint")}
        </p>
      )}
      <QuotationDocument quotation={q} />
      <Panel className="mt-3 p-4">
        <h2 className="mb-3 font-medium">{t("quotationWorkspace.history")}</h2>
        {!q.events?.length && (
          <p className="text-sm text-ink-faint">
            {t("quotationWorkspace.legacyHistory")}
          </p>
        )}
        <ol className="space-y-3">
          {q.events?.map((event) => (
            <li
              key={event.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-3 last:border-0"
            >
              <div>
                <p className="text-sm">
                  <span className="font-medium">
                    {t(`quotationWorkspace.event_${event.kind}`)}
                  </span>{" "}
                  · {t("quotationWorkspace.revision")}{" "}
                  <span className="font-mono">{event.revision}</span>
                </p>
                <p className="mt-1 text-xs text-ink-faint">
                  {event.actorName || "—"} ·{" "}
                  {formatDateTime(event.createdAt, i18n.language)}
                </p>
              </div>
              {event.kind === "Issued" && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={loadingRevision !== null}
                  onClick={() => void showRevision(event.revision)}
                >
                  {loadingRevision === event.revision
                    ? t("common.loading")
                    : t("quotationWorkspace.viewRevision")}
                </Button>
              )}
            </li>
          ))}
        </ol>
      </Panel>
      <ConfirmDialog
        open={confirm !== null}
        tone={confirm === "archive" ? "danger" : "warn"}
        title={t(`quotationWorkspace.${confirm ?? "archive"}`)}
        description={`${q.quotationNo} · ${t("quotationWorkspace.revision")} ${q.revision}. ${t(`quotationWorkspace.${confirm ?? "archive"}Confirm`)}`}
        confirmLabel={t(`quotationWorkspace.${confirm ?? "archive"}`)}
        cancelLabel={t("common.cancel")}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runAction()}
      />
      {exportOpen && (
        <DocumentExportDialog
          key={q.id}
          documentNo={`${q.quotationNo} · r${q.revision}`}
          onCsv={() => exportQuotationCsv(q, t)}
          onPdf={() => { setExportOpen(false); setPreview(q); }}
          onExcel={async () => {
            const { exportQuotationExcel } = await import("@/lib/exportQuotationExcel");
            return exportQuotationExcel(q);
          }}
          onClose={() => setExportOpen(false)}
        />
      )}
      {preview && (
        <Modal
          className="max-w-6xl"
          title={`${t("quotationWorkspace.preview")} · ${preview.quotationNo} · ${t("quotationWorkspace.revision")} ${preview.revision}`}
          closeLabel={t("common.close")}
          onClose={() => setPreview(null)}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 p-3">
            <SaveAsPdfHint />
            <span className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => exportQuotationCsv(preview, t)}
            >
              {t("common.export")}
            </Button>
            <Button onClick={() => printQuotation(preview)}>
              {t("common.print")}
            </Button>
            </span>
          </div>
          <iframe
            title={t("quotationWorkspace.preview")}
            sandbox="allow-same-origin"
            srcDoc={buildQuotationHtml(preview)}
            className="min-h-[70dvh] w-full bg-surface"
          />
        </Modal>
      )}
    </PageBody>
  );
}
